import { useEffect } from "react";
import { useRouter, useSegments } from "expo-router";
import { useAuth } from "../AuthContext";
import { useAcquisition } from "../AcquisitionContext";
import CustomActivityIndicator from "../../Components/CustomActivityIndicator";
import { View } from "react-native";

/**
 * AuthGuard - Protects routes from unauthenticated access
 * Redirects to /login if user is not authenticated
 * Redirects to /onboarding if user needs onboarding
 */
export const AuthGuard = ({ children }) => {
  const { isAuthenticated, loading, needsOnboarding } = useAuth();
  const { buildUrlWithParams } = useAcquisition();
  const router = useRouter();
  const segments = useSegments();

  useEffect(() => {
    if (loading) return;

    const inAuthRoute = segments[0] === "login" || segments[0] === "signup";
    const inOnboarding = segments[0] === "onboarding";
    const inCoachApplications =
      segments[0] && segments[0].startsWith("[coachApplications]");

    // Allow coach application routes (e.g., /coaching) without authentication
    if (inCoachApplications) {
      return;
    }

    if (!isAuthenticated && !inAuthRoute) {
      // User not authenticated, redirect to signup if trying to access onboarding, otherwise login
      if (inOnboarding) {
        router.replace(buildUrlWithParams("/signup"));
      } else {
        router.replace(buildUrlWithParams("/login"));
      }
    } else if (isAuthenticated && inAuthRoute) {
      // User is authenticated but trying to access auth pages (including nested routes like /login/forgot-password)
      if (needsOnboarding) {
        router.replace("/onboarding");
      } else {
        router.replace("/");
      }
    } else if (isAuthenticated && needsOnboarding && !inOnboarding) {
      // User needs onboarding, redirect to onboarding
      router.replace("/onboarding");
    } else if (isAuthenticated && !needsOnboarding && inOnboarding) {
      // User completed onboarding, redirect to home
      router.replace("/");
    }
  }, [isAuthenticated, loading, needsOnboarding, segments, buildUrlWithParams]);

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: "rgb(255,255,255)" }}>
        {/* <CustomActivityIndicator large={true} white={false} /> */}
      </View>
    );
  }

  return <>{children}</>;
};

/**
 * PublicGuard - For public-only routes (login, signup)
 * Redirects to / if user is already authenticated
 */
export const PublicGuard = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (loading) return;

    if (isAuthenticated) {
      // User is authenticated, redirect to home
      router.replace("/");
    }
  }, [isAuthenticated, loading]);

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: "rgb(255,255,255)" }}>
        {/* <CustomActivityIndicator large={true} white={false} /> */}
      </View>
    );
  }

  // If authenticated, return null (redirect will happen)
  if (isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};
